import { useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronDown, FlaskConical, LogOut, Menu, ShieldCheck } from 'lucide-react';
import { ThemeToggle } from '@/theme/ThemeToggle';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/lib/auth/AuthProvider';
import { API_TRANSPORT } from '@/lib/api/config';
import { initials } from '@/lib/format';
import { cn } from '@/lib/cn';

/*
  Application header: menu button on narrow viewports, transport banner, theme toggle, and
  the current user menu with sign out.

  The transport banner is not decoration. With the mock transport live, every number on
  screen is a fixture, and a console that looks real while showing invented data is how a
  demo becomes a false report. It is stated once, permanently, in the header.
*/

export interface HeaderProps {
  readonly onOpenNav: () => void;
}

export function Header({ onOpenNav }: HeaderProps): React.JSX.Element {
  const { user, signOut } = useAuth();
  const [signingOut, setSigningOut] = useState(false);

  async function handleSignOut(): Promise<void> {
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      setSigningOut(false);
    }
  }

  return (
    <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-3 border-b border-border bg-surface/95 px-3 backdrop-blur-sm sm:px-4">
      <Button
        variant="ghost"
        size="sm"
        onClick={onOpenNav}
        className="lg:hidden"
        aria-label="Open navigation menu"
      >
        <Menu className="size-4" aria-hidden="true" />
      </Button>

      <div className="min-w-0 flex-1">
        {API_TRANSPORT === 'mock' ? (
          <span
            className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-warning)]/40 bg-[var(--color-warning-soft)] px-2 py-1 text-xs font-semibold text-[var(--color-warning)]"
            title="VITE_API_TRANSPORT is mock. Every figure on screen comes from src/mocks/."
          >
            <FlaskConical className="size-3.5" aria-hidden="true" />
            <span className="hidden sm:inline">Fixture data, not a live tenant</span>
            <span className="sm:hidden">Fixtures</span>
          </span>
        ) : null}
      </div>

      <ThemeToggle />

      <DropdownMenu.Root>
        <DropdownMenu.Trigger
          className={cn(
            'inline-flex h-9 items-center gap-2 rounded-[var(--radius-control)] border border-border bg-surface px-2 text-sm',
            'text-fg transition-colors hover:bg-surface-hover',
          )}
          aria-label={`Account menu for ${user?.fullName ?? 'current user'}`}
        >
          <span
            aria-hidden="true"
            className="flex size-6 items-center justify-center rounded-full bg-accent text-[11px] font-bold text-accent-fg"
          >
            {initials(user?.fullName ?? '?')}
          </span>
          <span className="hidden max-w-36 truncate font-medium sm:inline">
            {user?.fullName ?? 'Signed in'}
          </span>
          <ChevronDown className="size-3.5 text-fg-subtle" aria-hidden="true" />
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={6}
            className="animate-pop-in z-50 w-72 rounded-[var(--radius-control)] border border-border bg-surface-raised p-1 shadow-lg"
          >
            <div className="px-2 py-2">
              <p className="truncate text-sm font-semibold text-fg">{user?.fullName ?? ''}</p>
              <p className="truncate text-xs text-fg-subtle">{user?.email ?? ''}</p>
              <p className="mt-1.5 flex items-center gap-1.5 text-xs text-fg-muted">
                <ShieldCheck className="size-3.5 text-[var(--color-success)]" aria-hidden="true" />
                {user?.roleNames.join(', ') ?? 'No role'}
              </p>
              <p className="mt-1 text-[11px] leading-relaxed text-fg-subtle">
                Factors this session: {user?.authMethods.join(', ') ?? 'unknown'}. Dangerous
                actions ask for a fresh second factor regardless.
              </p>
            </div>
            <DropdownMenu.Separator className="my-1 h-px bg-border" />
            <DropdownMenu.Item
              onSelect={(event) => {
                // Keep the menu mounted while the request is in flight so the disabled
                // state is visible rather than the menu vanishing mid-action.
                event.preventDefault();
                void handleSignOut();
              }}
              disabled={signingOut}
              className="flex cursor-default items-center gap-2 rounded px-2 py-1.5 text-sm text-fg select-none data-disabled:opacity-50 data-highlighted:bg-surface-hover data-highlighted:outline-none"
            >
              <LogOut className="size-4" aria-hidden="true" />
              {signingOut ? 'Signing out' : 'Sign out'}
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </header>
  );
}
